/**
 * Centralized task filtering for display (ETAP 1 refactoring).
 * Tasks are expected to have: scheduled_at (string ISO), status ('pending' | 'in_progress' | 'done' | 'cancelled'),
 * task_type (optional): 'sop_standard' | 'coordinator_single' | 'long_term' | 'employee_extra'.
 *
 * Business rules:
 * - One-off tasks (coordinator_single, employee_extra, long_term): if not done and in the past -> overdue (stay visible).
 * - SOP (sop_standard): valid only on the scheduled day; if past and not done -> ignore (do not show as overdue).
 * - Done tasks from the past: not shown on the work list (no history).
 */

export interface TaskFilterContext {
  todayStart: Date;
  todayEnd: Date;
}

export interface TaskFilterResult<T = unknown> {
  todayPending: T[];
  todayDone: T[];
  overdue: T[];
  future: T[];
}

/** Task type from cleaning_tasks; SOP expires after the day, others become overdue. */
export type DisplayTaskType = 'sop_standard' | 'coordinator_single' | 'long_term' | 'employee_extra' | 'extra_paid';

export interface TaskFilterInput {
  scheduled_at?: string | null;
  status?: string | null;
  task_type?: string | null;
}

/**
 * Returns the start of the task's scheduled day (00:00:00) for date comparison.
 */
function getTaskDateStart(scheduledAt: Date): Date {
  const d = new Date(scheduledAt);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Returns tasks grouped by display buckets: today pending, today done, overdue, future.
 * Uses scheduled_at for date comparison; tasks without scheduled_at are treated as "today".
 *
 * Past tasks (taskDate < todayStart):
 * - status === 'done' -> ignored (no history on work list).
 * - status !== 'done' && task_type === 'sop_standard' -> ignored (SOPs expire).
 * - status !== 'done' && other types -> added to overdue.
 */
export function filterTasksForDisplay<T extends TaskFilterInput>(
  tasks: T[],
  context: TaskFilterContext
): TaskFilterResult<T> {
  const { todayStart, todayEnd } = context;
  const todayPending: T[] = [];
  const todayDone: T[] = [];
  const overdue: T[] = [];
  const future: T[] = [];

  for (const task of tasks) {
    const scheduledAt = task.scheduled_at ? new Date(task.scheduled_at) : null;
    const status = task.status ?? 'pending';
    const taskType = (task.task_type ?? null) as DisplayTaskType | null;

    if (status === 'cancelled') continue;

    // No date: treat as "today" for display
    if (!scheduledAt || isNaN(scheduledAt.getTime())) {
      if (status === 'done') {
        todayDone.push(task);
      } else if (status === 'pending' || status === 'in_progress') {
        todayPending.push(task);
      }
      continue;
    }

    const taskDate = getTaskDateStart(scheduledAt);

    // Future: task date is after today
    if (taskDate > todayEnd) {
      future.push(task);
      continue;
    }

    // Past: task date is before today
    if (taskDate < todayStart) {
      if (status === 'done' || status === 'cancelled') {
        // Do not show past done/cancelled on work list
        continue;
      }
      if (taskType === 'sop_standard') {
        // SOP in_progress from the past -> overdue (worker has started, must finish)
        if (status === 'in_progress') {
          overdue.push(task);
          continue;
        }
        // SOP pending from the past -> expire (ignore, do not show as overdue)
        continue;
      }
      // Other types (coordinator_single, employee_extra, long_term) -> overdue
      overdue.push(task);
      continue;
    }

    // Today: taskDate is within [todayStart, todayEnd]
    if (status === 'done') {
      todayDone.push(task);
    } else if (status === 'pending' || status === 'in_progress') {
      todayPending.push(task);
    }
  }

  return {
    todayPending,
    todayDone,
    overdue,
    future,
  };
}
